import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, from, split } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { REACT_APP_GQL_API, REACT_APP_GQL_API_WS } from "src/config/environment"
import { getToken, clearSession } from "src/utils/session"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { createClient, Client, SubscribePayload, Sink } from "graphql-ws"
import { getMainDefinition } from "@apollo/client/utilities"

const httpLink = new HttpLink({
    uri: REACT_APP_GQL_API,
    //credentials: "include",
})

const authLink = new ApolloLink((operation, forward) => {
    const token = getToken()

    if (token) {
        const context = operation.getContext()
        const customHeaders = context?.headers || {}
        
        operation.setContext({
            headers: {
                ...customHeaders,
                Authorization: `Bearer ${token}`,
                "X-Client-URL": window.location.origin,
            },
        })
    }

    return forward(operation)
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.log(
                `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations, null, 2)}, Path: ${path}`
            )
            if(message.includes("jwt expired")) {
                clearSession()
                window.location.replace("/")
                window.location.reload()
            }
        })
    }
    if (networkError) console.log(`[Network error]: ${networkError}`)
})

// Crea un cliente graphql-ws con su propia detección de socket "zombie".
//
// El servidor puede dejar de responder los pings de keepAlive sin cerrar el socket
// (ej. dispositivo suspendido, cambio de red). graphql-ws no cierra la conexión
// "zombie" por sí solo: hay que forzar el cierre manualmente para que se dispare
// el retry automático (retryAttempts/retryWait).
//
// El estado del detector (activeSocket/pongTimeout) es propio de cada cliente (no
// module-level), para que al recrear el cliente (ver performReconnect) el cliente
// viejo, mientras se apaga, no pise el estado del nuevo.
//
// Se devuelve envuelto en un handle porque `activeSocket` también sirve para saber si la
// conexión está viva (isAlive), y ese estado vive dentro de este closure.
interface RawClientHandle {
    client: Client
    /**
     * true solo si este cliente tiene un socket conectado y abierto. false mientras
     * reconecta (CONNECTING), tras un cierre, o si nunca llegó a conectar.
     *
     * Ojo: un socket "zombie" (abierto pero que ya no responde) reporta OPEN hasta que la
     * verificación de ping/pong de más abajo lo cierra; ahí recién pasa a false.
     */
    isAlive: () => boolean
}

const createRawClient = (): RawClientHandle => {
    let activeSocket: WebSocket | undefined = undefined
    let pongTimeout: ReturnType<typeof setTimeout> | undefined = undefined

    const clearPongTimeout = () => {
        if (pongTimeout) {
            clearTimeout(pongTimeout)
            pongTimeout = undefined
        }
    }

    const client = createClient({
        url: REACT_APP_GQL_API_WS || "",
        retryAttempts: Infinity,
        keepAlive: 10_000,
        // Reintentar ante CUALQUIER cierre no fatal (red caída, socket "zombie", timeout).
        shouldRetry: () => true,
        retryWait: async (_retries) => {
            const retryDelay = 1000
            await new Promise((res) => setTimeout(res, retryDelay))
        },
        on: {
            connected: (socket) => {
                activeSocket = socket as WebSocket
                // Un socket nuevo y vivo invalida cualquier verificación de pong pendiente:
                // si no la cancelamos, un timeout viejo (ej. congelado con la pestaña en
                // segundo plano) cerraría este socket recién conectado con 4408.
                clearPongTimeout()
            },
            closed: () => {
                clearPongTimeout()
                activeSocket = undefined
            },
            ping: (received) => {
                if (received) {
                    return
                }
                clearPongTimeout()
                // Se ata la verificación al socket concreto al que le mandamos el ping.
                const pingedSocket = activeSocket
                pongTimeout = setTimeout(() => {
                    // Solo se cierra si es EXACTAMENTE ese socket y sigue abierto; así una
                    // verificación tardía nunca cierra una conexión posterior ya sana.
                    if (
                        pingedSocket &&
                        pingedSocket === activeSocket &&
                        pingedSocket.readyState === WebSocket.OPEN
                    ) {
                        pingedSocket.close(4408, "Request Timeout")
                    }
                }, 5_000)
            },
            pong: (received) => {
                if (received) {
                    clearPongTimeout()
                }
            },
        },
    })

    return {
        client,
        isAlive: () => activeSocket?.readyState === WebSocket.OPEN,
    }
}

// Handle del cliente graphql-ws "real" vigente. Se reemplaza por completo en
// performReconnect; el viejo se descarta junto con su estado.
let rawClient = createRawClient()

// Cada suscripción activa de Apollo. Guardamos su payload y su sink para poder
// re-engancharla a un cliente nuevo tras recrearlo, sin que Apollo se entere.
// `gen` invalida las callbacks del cliente viejo: cuando recreamos, subimos `gen`
// de todas las entradas, así el complete/error que dispara el cierre del cliente
// viejo se ignora en lugar de terminar la suscripción de Apollo.
interface ActiveSubscription {
    payload: SubscribePayload
    sink: Sink
    gen: number
    dispose: () => void
}

const activeSubscriptions = new Set<ActiveSubscription>()

const attach = (entry: ActiveSubscription) => {
    const gen = entry.gen
    entry.dispose = rawClient.client.subscribe(entry.payload, {
        next: (value) => {
            if (gen === entry.gen) entry.sink.next(value)
        },
        error: (err) => {
            if (gen !== entry.gen) return
            activeSubscriptions.delete(entry)
            entry.sink.error(err)
        },
        complete: () => {
            if (gen !== entry.gen) return
            activeSubscriptions.delete(entry)
            entry.sink.complete()
        },
    })
}

// Cliente delegador que ve el GraphQLWsLink. Reenvía cada suscripción al `rawClient`
// vigente y la registra para poder migrarla si el cliente se recrea.
const wsClient = {
    subscribe(payload: SubscribePayload, sink: Sink) {
        const entry: ActiveSubscription = { payload, sink, gen: 0, dispose: () => undefined }
        activeSubscriptions.add(entry)
        attach(entry)
        return () => {
            entry.gen++ // invalida callbacks en vuelo del cliente actual
            activeSubscriptions.delete(entry)
            entry.dispose()
        }
    },
} as unknown as Client

// Mata la conexión actual y crea una nueva DESDE CERO, re-enganchando las suscripciones
// activas al socket nuevo. A diferencia de `terminate()` (que depende del retry interno y
// no revive tras un cierre fatal), esto garantiza un socket nuevo sin importar el estado
// previo. Se usa antes de un refetch al volver a la ventana: se espera la confirmación de
// conexión ANTES del refetch, así una actualización concurrente llega por la suscripción
// (ya viva) después del refetch, en vez de que un refetch viejo la pise.
//
// No se llama directo: se pide vía requestWsReconnect, que aplica el límite de tiempo.
/**
 * @param timeoutMs Tope de espera por si la conexión nunca se confirma (ej. sin internet
 * real) — evita que el `await` de quien llama quede colgado, ya que retryAttempts: Infinity
 * reintentaría sin fin.
 */
const performReconnect = (timeoutMs = 8_000): Promise<void> => {
    return new Promise((resolve) => {
        const dying = rawClient

        // 1) Invalida las callbacks de las suscripciones actuales: el cierre del cliente
        //    viejo NO debe propagar complete/error a Apollo (las estamos migrando).
        const entries = [...activeSubscriptions]
        for (const entry of entries) {
            entry.gen++
        }

        // 2) Fuerza el cierre inmediato del socket vivo y detiene los reintentos del viejo.
        dying.client.terminate()
        Promise.resolve(dying.client.dispose()).catch(() => undefined)

        // 3) Crea un cliente nuevo y se re-suscribe todo contra él (mismos sinks de Apollo).
        rawClient = createRawClient()

        let settled = false
        let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined
        const finish = () => {
            if (settled) return
            settled = true
            clearTimeout(timeoutId)
            unsubscribe()
            resolve()
        }
        const unsubscribe = rawClient.client.on("connected", finish)
        timeoutId = setTimeout(finish, timeoutMs)

        for (const entry of entries) {
            attach(entry)
        }
    })
}

// Mínimo entre resincronizaciones al volver a la pestaña, mientras la conexión siga viva.
// Si se vuelve a la ventana antes de que pasen y el socket está sano, no se reconecta NI se
// refetchea: se deja todo como está y los cambios siguen llegando por la suscripción.
const RESYNC_MIN_INTERVAL_MS = 5 * 60_000

// Momento en que arrancó la última ronda de resync (0 = nunca, así la primera siempre pasa).
let lastResyncAt = 0

// Ronda en vuelo. Los providers (Rooms/Tables/Orders) escuchan visibilitychange cada uno
// por su lado pero comparten el mismo socket: el primero abre la ronda y los demás se
// cuelgan de esta misma promesa, así el socket se recrea UNA vez y los tres refetchean.
let pendingResync: Promise<void> | undefined = undefined

interface ReconnectOptions {
    /**
     * Ignora RESYNC_MIN_INTERVAL_MS aunque el socket se vea vivo. Para señales que implican
     * que la conexión puede haber quedado "zombie" sin que se note (ej. evento `online`: la
     * red se cayó y volvió). No usar al volver a la pestaña.
     */
    force?: boolean
}

/**
 * Pide una resincronización (socket nuevo + refetch de quien llama).
 *
 * Se resincroniza si pasaron RESYNC_MIN_INTERVAL_MS, O si la conexión está caída — sin
 * importar cuándo fue la última vez, porque con el socket muerto no llegó ninguna
 * actualización por suscripción y los datos en pantalla pueden estar viejos.
 *
 * IMPORTANTE: la decisión es sincrónica, así que hay que llamarla ANTES del primer `await`
 * del handler; si no, los providers dejan de compartir la misma ronda y cada uno recrearía
 * el socket por su cuenta.
 *
 * @returns la promesa de la reconexión, que hay que esperar antes de refetchear; o
 * `undefined` si la resincronización se descarta — en ese caso NO hay que refetchear ni
 * mostrar skeleton.
 */
export const requestWsReconnect = ({ force = false }: ReconnectOptions = {}): Promise<void> | undefined => {
    // Ronda ya abierta por otro provider en este mismo evento: se comparte.
    if (pendingResync) {
        return pendingResync
    }

    const throttled = Date.now() - lastResyncAt < RESYNC_MIN_INTERVAL_MS
    if (throttled && !force && rawClient.isAlive()) {
        
        return undefined
    }

    lastResyncAt = Date.now()

    pendingResync = performReconnect().finally(() => {
        pendingResync = undefined
    })

    return pendingResync
}

const wsLink = new GraphQLWsLink(wsClient)

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query)
        return definition.kind === "OperationDefinition" && definition.operation === "subscription"
    },
    wsLink,
    from([errorLink, authLink, httpLink])
)

export const client = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "no-cache",
        },
        query: {
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        },
    },
})
